import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { QueryPayloadSummary } from '../../api/schema';
import ActiveSwitchFilter from '../../component/filters/ActiveSwitchFilter';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import prepareParams from '../../helpers/prepareParams';
import usePeriodFilter from '../../hooks/usePeriodFilter';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getPayloadByWarehouseIdFetching, getPayloads, getPayloadsByWarehouse } from '../../redux/slices/payloads/payloadsSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';

export const Payloads: React.FC = () => {
  const dispatch = useDispatch();
  const { id: deviceId = '' } = useParams();
  const [params, setURLParams] = useSearchParams();
  const queryParams = useMemo(
    () =>
      Object.keys(params).reduce((partParams, key) => ({ ...partParams, [key]: prepareParams[key] ? prepareParams[key](params[key]) : params[key] }), {
        deviceId,
      } as { [key: string]: string }),
    [params, deviceId],
  );

  const payloads = useSelector(getPayloadsByWarehouse(queryParams));
  const fetching = useSelector(getPayloadByWarehouseIdFetching);
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: payloads.pagination });

  const PeriodFilter = usePeriodFilter({ params, setURLParams, maxRangeDays: 30, beginRequired: true, endRequired: true });

  useEffect(() => {
    if (isEmpty(params)) {
      setURLParams({
        newParams: {
          processedBegin: DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISO() as string,
          processedEnd: DateTime.local().set({ hour: 23, minute: 59, second: 59, millisecond: 99 }).toISO() as string,
        },
      });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (deviceId && queryParams.processedBegin && queryParams.processedEnd) {
      dispatch(
        getPayloads({
          ...queryParams,
          ...paginationToGet,
        }),
      );
    }
  }, [dispatch, deviceId, paginationToGet, queryParams]);

  const columns = useMemo(
    () => [
      {
        Header: 'Time',
        accessor: 'processed_at',
        Cell: ({ row }: { row: { original: QueryPayloadSummary } }) =>
          row.original.processed_at ? (
            <Link to={`/admin/organizations/devices/payloads/${row.original.payload_id}/details`}>
              {DateTime.fromISO(row.original.processed_at).toLocaleString(DateTime.DATETIME_MED)}
            </Link>
          ) : (
            ''
          ),
        Filter: PeriodFilter,
      },

      {
        Header: 'Job Site Detected',
        accessor: 'site_name',
      },
      {
        Header: 'Location Type',
        accessor: 'payload_type',
        Filter: ActiveSwitchFilter,
        filterProps: { label: 'Display Extended', param: 'extendedView', defaultActive: true, hideUncheckedLabel: true },
      },
      ...(params.extendedView === 'true'
        ? [
            {
              Header: 'Iteration',
              accessor: 'operation.iteration',
            },
            {
              Header: 'Begin Mode',
              accessor: 'operation.power_mode',
            },
            {
              Header: 'End Mode',
              accessor: 'operation.power_mode_end',
            },
            {
              Header: 'Trigger',
              accessor: 'operation.trigger',
            },
            {
              Header: 'End Volt.',
              accessor: 'environment.voltage_submit',
            },
          ]
        : []),
    ],
    [PeriodFilter, params.extendedView],
  );

  return (
    <>
      {fetching && <LinearProgress />}
      <Card>
        <Table
          columns={columns}
          data={payloads.result || []}
          loading={fetching}
          paginationControl={paginationControl}
          pagination={pagination}
          showDefaultFilters
          hideClearFilters
        />
      </Card>
    </>
  );
};
